import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useReactTable } from '@tanstack/react-table';

// Actions
import { allocateEvenly, allocateRemaining, setPortfolioAllocationMode, getComparisonItem } from '@/redux/modules/edit-portfolio';

// Components
import PortfolioEditorHoldingsTable from './components/PortfolioEditorHoldingsTable/PortfolioEditorHoldingsTable';
import PortfolioEditorMetricsTable from './components/PortfolioEditorMetricsTable/PortfolioEditorMetricsTable';
import PortfolioEditorPlanningAnalysis from './components/PortfolioEditorPlanningAnalysis/PortfolioEditorPlanningAnalysis';

import RiskProfileCompare from '@/views/containers/comparison/RiskProfile/RiskProfileCompare';
import { AllocationModeToggle } from '@/views/components/misc';
import { ControlItem, Tabs } from '@/views/components/navigation';
import { SearchInput, ToggleSwitch } from '@/views/components/inputs';
import { SecondaryLink } from '@/views/components/buttons';
import { Tooltip, WarningTooltip } from '@/views/components/tooltips';
import { withErrorBoundary } from '@/views/components/errors';

// Constants
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';

// Enums
import { HoldingAllocationMode, PORTFOLIO_HOLDINGS_KEYS } from '@/enums/Portfolio';

// Hooks
import usePrevious from '@/hooks/usePrevious';

// Libraries
import classNames from 'classnames';

// Selectors
import { selectShelfStatusedPortfolioHoldings } from '@/redux/modules/shelf-management/selectors';

// Types
import Locale from '@/types/locale';
import LocalizedString from '@/types/localized-string';
import Nullable from '@/types/nullable';
import Portfolio from '@/types/portfolio';
import PortfolioHolding from '@/types/portfolio-holding';
import Holding from '@/types/holding';
import User from '@/types/user';
import { ReduxState } from '@/redux/modules/types';
import ThemeMode from '@/types/theme-mode';
import ComparisonItem from '@/types/comparison-item';
import ProxyLogEntry from '@/types/portfolio-log-entry';

// Utils
import { HOLDINGS_EDITOR_TYPE } from '@/utils/constants';
import { canAccessAllocationModeToggle } from '@/utils/feature-access';
import { DEFAULT_DECIMALS, formatMessage, formatNumber, FormattedMessage } from '@/utils/i18n';
import { searchObjectsOnFields } from '@/utils/search';
import { getPortfolioMissingOrInvalidHoldings } from '@/utils/portfolio/utils';
import { canViewPlanning, isCanViewRiskProfile } from '@/utils/portfolio/portfolio';
import { isUserInLotRegion } from '@/utils/tax-analysis';

enum PORTFOLIO_EDITOR_CONTENT_TABS {
  HOLDINGS = 'holdings',
  METRICS = 'metrics',
  PLANNING = 'planning',
}

type DispatchProps = {
  allocateEvenly: () => void;
  allocateRemaining: () => void;
  getComparisonItem: () => Promise<ComparisonItem>;
  setPortfolioAllocationMode: (mode: HoldingAllocationMode) => void;
  onProxyLogChange: (log: ProxyLogEntry[]) => void;
};

type StateProps = {
  isPortfolioValueLocked?: boolean;
  isReadOnly?: boolean;
  locale: Locale;
  portfolio: Portfolio;
  portfolioHasMissingHoldings: boolean;
  user?: Nullable<User>;
  showTaxDetails?: boolean;
};

type Props = DispatchProps &
  StateProps & {
    totalAllocated: number;
    onChangePlanAccount: () => void;
    showMissingCostBasisBanner?: boolean;
    isMissingCostBasis: boolean;
    assetTypeCache: Map<string, LocalizedString>;
    getHoldingData: (holding: PortfolioHolding) => Promise<Holding>;
    displayClearHoldingsModal: () => void;
  };

const PortfolioEditorContent = ({
  isPortfolioValueLocked,
  isReadOnly,
  locale,
  portfolio,
  portfolioHasMissingHoldings,
  totalAllocated,
  user,
  allocateEvenly,
  allocateRemaining,
  getComparisonItem,
  setPortfolioAllocationMode,
  onProxyLogChange,
  onChangePlanAccount,
  showTaxDetails,
  showMissingCostBasisBanner,
  assetTypeCache,
  getHoldingData,
  displayClearHoldingsModal,
}: Props) => {
  const { allocationMode, holdings, size } = portfolio;

  const holdingEditorType = HOLDINGS_EDITOR_TYPE.PORTFOLIO;
  const isEmptyHoldingsTable = !holdings.length;
  const i18nBasePath = 'PortfolioEditorBuilder.Content';
  const previousHoldings = usePrevious(holdings) || [];
  const roundedSize: number | undefined = size ? +size.toFixed(2) : undefined;
  const isInLotRegion = isUserInLotRegion(user);

  const [filteredHoldings, setFilteredHoldings] = useState<PortfolioHolding[]>(holdings);
  const [portfolioSearchQuery, setPortfolioSearchQuery] = useState<string>('');
  const [selectedHoldingsTab, setSelectedHoldingsTab] = useState<PORTFOLIO_EDITOR_CONTENT_TABS>(PORTFOLIO_EDITOR_CONTENT_TABS.HOLDINGS);
  const [comparedItem, setComparedItem] = useState<Nullable<ComparisonItem>>(null);
  const [showRiskProfileView, setShowRiskProfileView] = useState<boolean>(false);
  const tableRef = useRef<ReturnType<typeof useReactTable> | null>(null);

  // Set initial state after initial render
  useEffect(() => {
    setFilteredHoldings(holdings);
    handleSearchWhenHoldingsChange();
    resetTableIfNoHoldings();

    if (isCanViewRiskProfile(user)) {
      const fetchComparisonItem = async () => {
        setComparedItem(await getComparisonItem());
      };
      fetchComparisonItem();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdings]);

  // SEARCH PORTFOLIO LOGIC

  const handleSearchWhenHoldingsChange = () => {
    const isHoldingAdded = holdings.length > previousHoldings.length;
    if (isHoldingAdded) {
      setPortfolioSearchQuery('');
      return;
    }

    const mustPerformSearchAgain = !isEmptyHoldingsTable && portfolioSearchQuery;
    if (mustPerformSearchAgain) {
      onSearchPortfolio(portfolioSearchQuery);
    }
  };

  const resetTableIfNoHoldings = () => {
    if (isEmptyHoldingsTable) {
      setPortfolioSearchQuery('');
      setSelectedHoldingsTab(PORTFOLIO_EDITOR_CONTENT_TABS.HOLDINGS);
    }
  };

  const clearSearchPortfolio = () => {
    setFilteredHoldings(holdings);
    return setPortfolioSearchQuery('');
  };

  const onSearchPortfolio = (query: string) => {
    if (!query) {
      return clearSearchPortfolio();
    }

    const searchableFields = [`${PORTFOLIO_HOLDINGS_KEYS.NAME}.${locale}`, PORTFOLIO_HOLDINGS_KEYS.SYMBOL];
    const searchResult: PortfolioHolding[] = searchObjectsOnFields(query, holdings, searchableFields);

    setFilteredHoldings(searchResult);
    setPortfolioSearchQuery(query);
  };

  // TABS LOGIC

  const onContentTabChange = (holdingsTab: PORTFOLIO_EDITOR_CONTENT_TABS) => {
    setSelectedHoldingsTab(holdingsTab);
  };

  const getContentTabsOptions = () => {
    const onlyEnableHoldingsTab = isEmptyHoldingsTable || portfolioHasMissingHoldings || isReadOnly;
    return [
      {
        value: PORTFOLIO_EDITOR_CONTENT_TABS.HOLDINGS,
        i18nPath: `${i18nBasePath}.Tabs.Holdings`,
      },
      {
        value: PORTFOLIO_EDITOR_CONTENT_TABS.METRICS,
        i18nPath: `${i18nBasePath}.Tabs.Metrics`,
        disabled: onlyEnableHoldingsTab,
      },
      {
        value: PORTFOLIO_EDITOR_CONTENT_TABS.PLANNING,
        i18nPath: `${i18nBasePath}.Tabs.Planning`,
        disabled: onlyEnableHoldingsTab,
        hidden: !canViewPlanning(user, portfolio?.contact),
      },
    ].filter(option => !option.hidden);
  };

  // ALLOCATION LOGIC
  const onAllocateEvenly = () => {
    allocateEvenly();
  };

  const onAllocateRemaining = () => {
    allocateRemaining();
  };

  const onChangeAllocationMode = (mode: HoldingAllocationMode) => {
    setPortfolioAllocationMode(mode);
  };

  const renderAllocationControl = () => {
    const hasEmptyHolding = holdings.some(h => !h.weight);
    const isAllocationModeWeight = allocationMode === HoldingAllocationMode.Weight;
    const isAllocateEvenly =
      isAllocationModeWeight && !!roundedSize && ((hasEmptyHolding && (totalAllocated === 0 || totalAllocated === 100)) || totalAllocated > 100);
    const isAllocateRemaining = isAllocationModeWeight && !!roundedSize && totalAllocated > 0 && totalAllocated < 100;

    const holdingsValue = holdings.reduce((acc, holding) => acc + (holding.dollars || 0), 0);

    const formattedHoldingsValue = formatNumber(holdingsValue, DEFAULT_DECIMALS, locale);

    const portfolioSize = portfolio.size;

    const shouldDisplayAllocatedWarning = portfolioSize && holdingsValue && portfolioSize !== holdingsValue;

    let allocationCallback: () => void = () => {};
    let allocationControlElement: JSX.Element = <></>;
    let allocationLabel: string = '';

    // If portfolio has 0% or 100% (or greater) allocated, offer to allocate (or reallocate) evenly.
    if (isAllocateEvenly) {
      allocationCallback = onAllocateEvenly;
      allocationLabel = `${i18nBasePath}.AllocationActions.${totalAllocated === 100 ? 'ReallocateEvenly' : 'AllocateEvenly'}`;
    }

    // If portfolio has anything between 0% and 100% allocated, offer to allocate remaining.
    if (isAllocateRemaining) {
      allocationCallback = onAllocateRemaining;
      allocationLabel = `${i18nBasePath}.AllocationActions.AllocateRemaining`;
    }

    if (isAllocateEvenly || isAllocateRemaining) {
      const allocatedPercentageText = `(${totalAllocated}% ${formatMessage('PortfolioEditorBuilder.Content.AllocationActions.Allocated')})`;
      const allocationLabelElement = <FormattedMessage id={allocationLabel} />;
      const shouldDisableForTaxDetails = showTaxDetails && isInLotRegion;

      const content = (
        <div className={classNames('portfolio-editor-content-allocation-control-container', { 'is-disabled': shouldDisableForTaxDetails })}>
          {renderAllocationWarning()}
          <SecondaryLink className="secondary-link-v2" onClick={allocationCallback} disabled={shouldDisableForTaxDetails}>
            {allocationLabelElement}
          </SecondaryLink>
          <span className="portfolio-editor-content-total-allocated-percentage">{allocatedPercentageText}</span>
        </div>
      );

      allocationControlElement = shouldDisableForTaxDetails ? (
        <Tooltip className="custom-tooltip-v2 tax-details-tooltip" message={formatMessage(`${i18nBasePath}.AllocationActions.DisabledByTaxDetails`)}>
          {content}
        </Tooltip>
      ) : (
        content
      );
    }

    if (
      allocationMode === HoldingAllocationMode.Units &&
      userHasPermission(user, PERMISSIONS.PORTFOLIO_EDITOR_LOCK_TOTAL_VALUE) &&
      isPortfolioValueLocked &&
      shouldDisplayAllocatedWarning
    ) {
      return (
        <div className="portfolio-editor-content-allocation-control-container">
          {renderAllocationWarning()}
          <span className="portfolio-editor-content-allocated-value">
            ${formattedHoldingsValue} {formatMessage('PortfolioEditorBuilder.Content.AllocationActions.Allocated')}
          </span>
        </div>
      );
    }

    return allocationControlElement;
  };

  const renderAllocationModeToggle = () => {
    if (!user || isEmptyHoldingsTable || !canAccessAllocationModeToggle(user)) {
      return null;
    }

    return (
      <AllocationModeToggle
        className="allocation-mode-v2"
        disabled={isReadOnly || portfolioHasMissingHoldings}
        onChange={onChangeAllocationMode}
        value={allocationMode}
      />
    );
  };

  const renderAllocationWarning = () => {
    let i18nKey: string = 'MissingAllocation';

    if (totalAllocated > 100) {
      i18nKey = 'OverAllocation';
    } else if (totalAllocated < 100) {
      i18nKey = 'UnderAllocation';
    }

    return <WarningTooltip className="custom-tooltip-v2" message={formatMessage(`${i18nBasePath}.AllocationActions.${i18nKey}.${holdingEditorType}`)} />;
  };

  // HOLDINGS / METRICS TABLE RENDER FUNCTION

  const renderContentTable = () => {
    switch (selectedHoldingsTab) {
      case PORTFOLIO_EDITOR_CONTENT_TABS.HOLDINGS: {
        return (
          <PortfolioEditorHoldingsTable
            allocationMode={allocationMode}
            showMissingCostBasisBanner={showMissingCostBasisBanner}
            onProxyLogChange={onProxyLogChange}
            portfolioHoldings={filteredHoldings}
            portfolioSearchQuery={portfolioSearchQuery}
            showTaxDetails={showTaxDetails}
            tableRef={tableRef}
            assetTypeCache={assetTypeCache}
            getHoldingData={getHoldingData}
            displayClearHoldingsModal={displayClearHoldingsModal}
          />
        );
      }
      case PORTFOLIO_EDITOR_CONTENT_TABS.METRICS: {
        return <PortfolioEditorMetricsTable portfolioHoldings={filteredHoldings} portfolioSearchQuery={portfolioSearchQuery} />;
      }
      case PORTFOLIO_EDITOR_CONTENT_TABS.PLANNING: {
        return <PortfolioEditorPlanningAnalysis onChangePlanAccount={onChangePlanAccount} contact={portfolio?.contact} />;
      }
      default: {
        return null;
      }
    }
  };

  // RISK PROFILE LOGIC

  const onCloseRiskProfileView = () => {
    setShowRiskProfileView(false);
  };

  const renderRiskProfileView = () => {
    if (
      selectedHoldingsTab !== PORTFOLIO_EDITOR_CONTENT_TABS.HOLDINGS ||
      isEmptyHoldingsTable ||
      !comparedItem ||
      !isCanViewRiskProfile(user) ||
      !showRiskProfileView
    ) {
      return null;
    }

    const { riskProfile } = portfolio;

    if (!riskProfile || !riskProfile?.profile) {
      return;
    }

    return (
      <div className="portfolio-editor-content-risk-profile">
        <RiskProfileCompare
          onPortfolio
          profile={riskProfile.profile}
          comparedItem={comparedItem}
          readOnly // We don't want the menu options in this case
          onClose={onCloseRiskProfileView}
          user={user}
          theme={ThemeMode.LIGHT}
        />
      </div>
    );
  };

  const renderViewRiskProfileToggle = () => {
    if (!user || !isCanViewRiskProfile(user) || isEmptyHoldingsTable) {
      return null;
    }

    return (
      <ToggleSwitch
        checked={showRiskProfileView}
        className="toggle-switch-v2 portfolio-editor-content-risk-profile-toggle"
        disabled={!portfolio?.riskProfile}
        icon="checkmark"
        labelPosition="right"
        onChange={setShowRiskProfileView}
        aria-label={formatMessage('PortfolioEditorBuilder.RiskProfile.ViewRiskProfileLabel')}
      >
        <span>{formatMessage('PortfolioEditorBuilder.RiskProfile.ViewRiskProfileLabel')}</span>
      </ToggleSwitch>
    );
  };

  const shouldDisplayRiskProfileView = isCanViewRiskProfile(user) && showRiskProfileView && selectedHoldingsTab === PORTFOLIO_EDITOR_CONTENT_TABS.HOLDINGS;

  return (
    <div className="portfolio-editor-content-container">
      <div className="portfolio-editor-content-header">
        <div className="portfolio-editor-content-upper-header">
          <h3 className="portfolio-editor-content-header-title">
            <FormattedMessage id={`${i18nBasePath}.Title`} />
          </h3>
          <div className="portfolio-editor-content-search-portfolio">
            <SearchInput
              className="search-input-v2"
              disabled={isEmptyHoldingsTable || portfolioHasMissingHoldings}
              onChange={onSearchPortfolio}
              placeholder={formatMessage(`${i18nBasePath}.SearchPortfolio`)}
              rightSection={portfolioSearchQuery ? <i className="icon-cross-thin clear-term" onClick={clearSearchPortfolio} /> : undefined}
              value={portfolioSearchQuery}
            />
          </div>
        </div>
        <div className="portfolio-editor-content-lower-header">
          <div className="tabs-v2-scrollable">
            <Tabs className="tabs-v2" onChange={onContentTabChange} selected={selectedHoldingsTab}>
              {getContentTabsOptions().map(option => (
                <ControlItem disabled={option.disabled} key={option.value} value={option.value}>
                  <FormattedMessage id={option.i18nPath} />
                </ControlItem>
              ))}
            </Tabs>
          </div>
          <div className="portfolio-editor-content-allocation-actions">
            {renderAllocationControl()}
            {renderAllocationModeToggle()}
            {renderViewRiskProfileToggle()}
          </div>

          <span
            aria-live="polite"
            className="visually-hidden"
          >{`${portfolio?.holdings.length} ${formatMessage('Common.Accessibility.HoldingsInPortfolio')}`}</span>
        </div>
      </div>
      <div
        className={classNames('portfolio-editor-content-body', {
          'has-missing-holdings': portfolioHasMissingHoldings,
          'has-risk-profile': shouldDisplayRiskProfileView,
        })}
      >
        {renderContentTable()}
        {renderRiskProfileView()}
      </div>
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => {
  const statusedPortfolioHoldings = selectShelfStatusedPortfolioHoldings(state.editPortfolio.portfolio);
  const missingHoldings = getPortfolioMissingOrInvalidHoldings(statusedPortfolioHoldings) || [];

  return {
    isReadOnly: state.comparison.portfolioEditor.isReadOnly,
    locale: state.intl.locale,
    portfolio: statusedPortfolioHoldings,
    portfolioHasMissingHoldings: !!missingHoldings.length,
    isPortfolioValueLocked: state.editPortfolio.isPortfolioValueLocked,
    showTaxDetails: state.editPortfolio.showTaxDetails,
    user: state.auth.user,
  };
};

export default connect(mapStateToProps, {
  allocateEvenly,
  allocateRemaining,
  getComparisonItem,
  setPortfolioAllocationMode,
})(withErrorBoundary(PortfolioEditorContent));
